import React, { useEffect, useState } from 'react';
import type { VisitsSourceCard } from '@troia-v3/types';

import { Button, Drawer, Input, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { getViewsProviderById, viewsProviders, type ViewsProviderMeta } from '@/providers/views';

import { viewSourcesApi } from '../services/viewSourcesApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
  /** Presente = editar esta fonte; ausente = conectar uma nova. */
  editing?: VisitsSourceCard | null;
}

/** Separa os campos do formulário entre `config` e `credentials`. */
const splitValues = (
  provider: ViewsProviderMeta,
  values: Record<string, string>,
): { config: Record<string, unknown>; credentials: Record<string, unknown> } => {
  const configNames = new Set(provider.configFields ?? []);
  const config: Record<string, unknown> = {};
  const credentials: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    // Campo em branco não é enviado: na edição isso significa "manter o atual",
    // e é a única saída possível para a senha, que a API nunca devolve.
    if (value.trim().length === 0) continue;
    if (configNames.has(key)) config[key] = value;
    else credentials[key] = value;
  }
  return { config, credentials };
};

/**
 * Valor de TODAS as opções liga/desliga do provedor, inclusive as desligadas.
 * Mandar o `false` explícito é o que permite desligar na edição — a config é
 * mesclada campo a campo no backend, e um campo ausente manteria o `true` salvo.
 */
const optionValues = (provider: ViewsProviderMeta, options: Record<string, boolean>): Record<string, boolean> =>
  Object.fromEntries((provider.optionFields ?? []).map((field) => [field.name, options[field.name] === true]));

/** A config salva pode trazer a opção como booleano ou como o texto `'true'`. */
const isOptionOn = (stored: unknown): boolean => stored === true || stored === 'true';

/**
 * Conectar uma fonte de visitas. Dois passos: escolher o provedor, preencher
 * credenciais. Molde estrutural: `CreateDatabaseModal` (que também é um Drawer).
 *
 * ⚠️ O tratamento de erro segue o de CANAIS, não o de bases de dados. Em bases,
 * credencial errada cria o recurso, fecha o drawer com sucesso e deixa a
 * integração em `status: 'error'` que a UI nunca lê. Aqui o erro é mostrado no
 * drawer e ele NÃO fecha.
 */
export const ConnectViewSourceDrawer: React.FC<Props> = ({ isOpen, onClose, onConnected, editing }) => {
  const [selected, setSelected] = useState<ViewsProviderMeta | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [options, setOptions] = useState<Record<string, boolean>>({});
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isEditing = Boolean(editing);

  useEffect(() => {
    if (!isOpen) {
      setSelected(null);
      setValues({});
      setOptions({});
      setName('');
      setError(null);
      return;
    }
    if (!editing) return;

    // Edição já entra no passo 2 — trocar o provedor de uma fonte existente não
    // é editar, é conectar outra coisa (o histórico de visitas é chaveado pelo
    // provider). Pré-preenche o que a API devolve: só o `config`.
    // Provedor fora do catalogo do frontend (tracker de site, API publica):
    // `provider` vem `null` e o passo 1 NAO pode aparecer — escolher outro
    // provedor nao e editar. O corpo cai no formulario reduzido abaixo.
    const provider = getViewsProviderById(editing.providerId) ?? null;
    setSelected(provider);
    setName(editing.name);
    const prefill: Record<string, string> = {};
    for (const field of provider?.authFields ?? []) {
      const stored = editing.config?.[field.name];
      if (typeof stored === 'string') prefill[field.name] = stored;
    }
    setValues(prefill);
    setOptions(
      Object.fromEntries(
        (provider?.optionFields ?? []).map((field) => [field.name, isOptionOn(editing.config?.[field.name])]),
      ),
    );
    setError(null);
  }, [isOpen, editing]);

  // Na edição, campo obrigatório em branco significa "manter o atual" — exigir
  // que a senha seja redigitada só para corrigir o endereço seria hostil.
  const missingRequired = isEditing
    ? false
    : (selected?.authFields.filter((f) => f.required).some((f) => !values[f.name]?.trim()) ?? true);

  const handleSubmit = async (): Promise<void> => {
    if (!selected && !editing) return;
    setError(null);
    setSaving(true);
    try {
      // Sem provedor no catalogo so da para renomear — e e exatamente o que o
      // backend aceita para fontes que EMPURRAM dado (tracker, API publica).
      const { config: fieldConfig, credentials } = selected
        ? splitValues(selected, values)
        : { config: {}, credentials: {} };
      const config = selected ? { ...fieldConfig, ...optionValues(selected, options) } : fieldConfig;
      if (editing) {
        await viewSourcesApi.update(editing.integrationId, {
          name: name.trim() || editing.name,
          ...(Object.keys(config).length > 0 ? { config } : {}),
          ...(Object.keys(credentials).length > 0 ? { credentials } : {}),
        });
      } else if (selected) {
        await viewSourcesApi.connect({
          providerId: selected.id,
          name: name.trim() || selected.name,
          config,
          credentials,
        });
      }
      onConnected();
      onClose();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message;
      setError(message || (editing ? 'Não foi possível salvar' : 'Não foi possível conectar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={editing ? 'Editar fonte de visitas' : 'Conectar fonte de visitas'}
      subtitle={selected ? selected.name : 'Escolha de onde vêm os dados de visitantes'}
      icon={<Icon name="activity" />}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving} data-testid="connect-source-cancel">
            Cancelar
          </Button>
          {(selected || isEditing) && (
            <Button
              onClick={() => void handleSubmit()}
              disabled={saving || missingRequired}
              data-testid="connect-source-submit"
            >
              {saving ? (editing ? 'Salvando…' : 'Conectando…') : editing ? 'Salvar' : 'Conectar'}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4 px-6 py-5" data-testid="connect-source-drawer">
        {error && (
          <div
            className="rounded-lg px-3 py-2 text-sm"
            style={{
              backgroundColor: 'var(--theme-error-subtle)',
              color: 'var(--theme-error-fg)',
              border: '1px solid var(--theme-error-subtle)',
            }}
            data-testid="connect-source-error"
          >
            {error}
          </div>
        )}

        {isEditing && !selected ? (
          <>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Nome da fonte
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="connect-source-name"
              />
            </div>
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              Esta fonte não tem credenciais para editar por aqui — só o nome. Para trocar a
              configuração dela, desconecte e conecte de novo.
            </p>
          </>
        ) : !selected ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {viewsProviders.map((provider) => (
              <button
                key={provider.id}
                type="button"
                disabled={!provider.isAvailable}
                onClick={() => setSelected(provider)}
                className="text-left rounded-xl p-3 flex gap-3 items-start transition-opacity disabled:opacity-55 disabled:cursor-not-allowed"
                style={{
                  border: '1px solid var(--theme-card-border)',
                  backgroundColor: 'var(--theme-card-background)',
                }}
                data-testid={`connect-source-option-${provider.id}`}
              >
                <span
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${provider.color}20`, color: provider.color }}
                >
                  <Icon name={provider.icon} size={18} />
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    className="flex items-center gap-1.5 text-sm font-semibold"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {provider.name}
                    {!provider.isAvailable && (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={{
                          backgroundColor: 'var(--theme-surface-sunken)',
                          color: 'var(--theme-muted-text)',
                        }}
                      >
                        em breve
                      </span>
                    )}
                  </span>
                  <span className="block text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                    {provider.description}
                  </span>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Nome da fonte
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={selected.name}
                data-testid="connect-source-name"
              />
            </div>

            {selected.authFields.map((field) => {
              // Campo de credencial na edição chega vazio porque a API não
              // devolve segredo — o texto precisa dizer isso, senão parece que
              // o cadastro se perdeu.
              const keepsCurrent = isEditing && !(selected.configFields ?? []).includes(field.name);
              return (
                <div key={field.name}>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                    {field.label}
                  </label>
                  <Input
                    type={field.type}
                    value={values[field.name] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    placeholder={keepsCurrent ? 'Deixe em branco para manter' : field.placeholder}
                    data-testid={`connect-source-field-${field.name}`}
                  />
                  {(keepsCurrent || field.helpText) && (
                    <p className="mt-1 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                      {keepsCurrent ? 'Preencha só se quiser trocar o valor atual.' : field.helpText}
                    </p>
                  )}
                </div>
              );
            })}

            {(selected.optionFields ?? []).map((field) => (
              <Switch
                key={field.name}
                label={field.label}
                description={field.helpText}
                checked={options[field.name] === true}
                onChange={(checked) => setOptions((prev) => ({ ...prev, [field.name]: checked }))}
                data-testid={`connect-source-option-field-${field.name}`}
              />
            ))}

            {!isEditing && (
              <button
                type="button"
                className="text-xs self-start underline"
                style={{ color: 'var(--theme-muted-text)' }}
                onClick={() => setSelected(null)}
                data-testid="connect-source-back"
              >
                Escolher outra fonte
              </button>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

ConnectViewSourceDrawer.displayName = 'ConnectViewSourceDrawer';
